import {Component, OnInit, Input } from '@angular/core';
import {Sidenavaction} from '../../../assets/js/application/sidenavaction';
import {Router} from '@angular/router';
import { NavigationToggleService } from 'src/app/services/navigation-toggle.service';
import { AccessControllerService } from 'src/app/ACL/roleAccessController.service';
import { MenuNavigationControllerService } from 'src/app/ACL/navigationController.service';

declare var $: any;

/**
 * LeftnavComponent
 * 
 * A navigation sidebar component that displays the main application menu structure
 * based on user role, business type, and access control policies. Handles dynamic
 * navigation structure initialization, menu state management, and responsive layout.
 * 
 * Key responsibilities:
 * - Display role-based navigation menu
 * - Manage sidebar collapse/expand state
 * - Handle responsive design (desktop/mobile views)
 * - Initialize navigation structure based on user permissions
 * - Navigate to different features based on user access
 * - Track user and business information from localStorage
 * - Initialize jQuery scroll behavior for menu items
 * 
 * Features:
 * - Dynamic navigation structure based on access control
 * - Sidebar collapse/expand with state subscription
 * - Responsive design detection (768px breakpoint)
 * - Mobile user agent detection for device-specific behavior
 * - Custom scroll bar for menu items using jQuery mCustomScrollbar
 * - Menu item navigation with role-based access control
 * 
 * User information displayed:
 * - Login username (LoginUserName)
 * - Person type (Farmer, Provider, Admin, etc.)
 * - Business name and category (Farm, Provider, Others)
 * 
 * Navigation structure updated dynamically based on:
 * - User person type
 * - Business category
 * - Available role permissions
 * - Feature subscriptions
 * - Access control policies
 * 
 * @component
 * @selector app-leftnav
 * @example
 * <app-leftnav [isOpen]="sidenavOpen"></app-leftnav>
 * 
 * @implements {OnInit, AfterViewInit}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-leftnav',
  templateUrl: './leftnav.component.html',
  styleUrls: ['./leftnav.component.scss']
})
export class LeftnavComponent implements OnInit {
  /** 
   * Login username retrieved from localStorage
   * Displayed in the navigation header
   * @type {string}
   */
  public LoginUserName: string;
  
  /** 
   * Person type of the logged-in user (Farmer, Provider, Admin, etc.)
   * Retrieved from localStorage and used for role-based menu display
   * @type {string}
   */
  public loginPersonType: string;
  
  /** 
   * Business name associated with the logged-in user
   * Retrieved from localStorage
   * @type {string}
   */
  public loginBusinessName: string;
  
  /** 
   * Alias for login business name
   * Used for display purposes in the navigation header
   * @type {string}
   */
  public LoginUserBusinessName: string;
  
  /** 
   * Business category (Farm, Provider, Others)
   * Retrieved from localStorage and used for role-based routing
   * @type {string}
   */
  public loginBusinessCategory: string;
  
  /** 
   * Person type used internally for permission checks
   * Synced from localStorage
   * @type {string}
   */
  public personType: string;
  
  /** 
   * Number of business admin people for permission checks
   * Used in role-based access control
   * @type {string}
   */
  public businessAdminPeople: string;
  
  /** 
   * Number of business admin customers for permission checks
   * Used in role-based access control
   * @type {string}
   */
  public businessAdminCustomers: string;
  
  /** 
   * Application permission object containing role-based permissions
   * Populated by AccessControllerService
   * @type {any}
   */
  public appPermission: any;
  
  /** 
   * Input property indicating if the sidebar is open
   * Used for responsive sidebar toggle
   * @type {boolean}
   * @default false
   * @input
   */
  @Input() isOpen: boolean = false;
  
  /** 
   * Sidebar collapse state
   * Managed by NavigationToggleService subscription
   * @type {boolean}
   * @default false
   */
  isCollapsed = false;
  
  /** 
   * Desktop view detection based on window width
   * Uses 768px breakpoint to determine responsive layout
   * @type {boolean}
   * @default true (if window.innerWidth > 768)
   */
  isDesktopView: boolean = window.innerWidth > 768;  // Check for desktop view by default
  
  /** 
   * Navigation structure array containing menu items and their hierarchy
   * Populated dynamically from AccessControllerService based on user role
   * Contains menu items, submenus, and access control metadata
   * @type {Array<any>}
   */
  public navStructure: any[] = [];
  /**
   * Constructor for LeftnavComponent
   * 
   * Initializes the left navigation sidebar component with required services
   * for navigation control, access management, and state management.
   * 
   * Services injected:
   * - Sidenavaction: Sidebar navigation action handler
   * - Router: Angular router for navigation
   * - NavigationToggleService: Manages sidebar collapse/expand state
   * - AccessControllerService: Provides role-based access control and navigation structure
   * - MenuNavigationControllerService: Handles menu item navigation
   * 
   * @constructor
   * @param {Sidenavaction} sidenavaction - Service for sidebar actions
   * @service Sidenavaction
   * @param {Router} router - Angular router service
   * @service Router
   * @param {NavigationToggleService} navToggleService - Service for sidebar toggle state
   * @service NavigationToggleService
   * @param {AccessControllerService} accessControllerService - Service for role-based access control
   * @service AccessControllerService
   * @param {MenuNavigationControllerService} menuNavigationControllerService - Service for menu navigation
   * @service MenuNavigationControllerService
   */
  constructor(public sidenavaction: Sidenavaction, public router: Router, private navToggleService: NavigationToggleService,
    private accessControllerService: AccessControllerService, private menuNavigationControllerService: MenuNavigationControllerService
  ) {
  }

  /**
   * Angular lifecycle hook - Component initialization
   * 
   * Performs the following initialization steps:
   * 1. Subscribes to sidebar collapse state from NavigationToggleService
   * 2. Retrieves user information from localStorage (loginPersonType, loginBusinessCategory)
   * 3. Retrieves business and user information from localStorage if available
   * 4. Initializes navigation structure based on user role and permissions
   * 
   * localStorage keys accessed:
   * - loginPersonType: Person type (Farmer, Provider, Admin, etc.)
   * - loginBusinessCategory: Business category (Farm, Provider, Others)
   * - LoginUserName: Display name of logged-in user
   * - loginBusinessName: Name of user's business
   * 
   * Subscription established:
   * - navToggleService.isCollapsed$: Observable for sidebar collapse state changes
   * 
   * Navigation structure:
   * - Built dynamically by accessControllerService based on user role
   * - Updated to reflect user permissions and available features
   * 
   * @method ngOnInit
   * @returns {void}
   * @lifecycle Angular component initialization hook - Called once after component creation
   */
  public ngOnInit() {
    this.navToggleService.isCollapsed$.subscribe(
      (collapsed) => this.isCollapsed = collapsed
    );
    // loginperson type details
    this.loginPersonType = localStorage.getItem('loginPersonType');
    this.loginBusinessCategory = localStorage.getItem('loginBusinessCategory');
    if (localStorage.length > 0) {
      this.personType = localStorage.getItem('loginPersonType');
      this.LoginUserName = localStorage.getItem('LoginUserName');
      this.LoginUserBusinessName = localStorage.loginBusinessName;
    }
    this.initializeNavStructure();
  }

  /**
   * Initializes the navigation menu structure based on user role and permissions
   * 
   * Retrieves the role-based navigation structure from the AccessControllerService.
   * The structure includes menu items, submenus, and feature availability based on:
   * - User person type
   * - Business category
   * - Access control policies
   * - Feature subscriptions
   * - Role-based permissions
   * 
   * Navigation structure format:
   * - Array of menu items with nested hierarchy
   * - Each item contains: label, route, icon, children (submenus), permissions
   * - Only items the user has access to are included
   * 
   * Called during:
   * - ngOnInit() for initial setup
   * - After access control policies are updated
   * 
   * @method initializeNavStructure
   * @returns {void}
   * @side-effect Updates this.navStructure with current role-based menu
   */
  public initializeNavStructure() {
    this.navStructure = this.accessControllerService.getNavigationStructure();
  }

  /**
   * Navigates to a specific feature/menu item
   * 
   * Handles menu item click navigation by delegating to the MenuNavigationControllerService.
   * The service handles:
   * - Route construction based on feature
   * - Access control verification
   * - Navigation state management
   * - Sidebar state updates (collapse/expand)
   * 
   * Features are defined by the navigation structure and can include:
   * - Dashboard, Reports, Settings
   * - Farm/Provider specific features
   * - Role-specific menu items
   * 
   * Navigation flow:
   * 1. Menu item clicked with feature identifier
   * 2. Feature passed to MenuNavigationControllerService
   * 3. Service verifies access and constructs route
   * 4. Router navigates to destination
   * 5. Sidebar may collapse based on configuration
   * 
   * @method menuNavigation
   * @param {any} feature - Feature identifier or menu item object to navigate to
   * @returns {void}
   * @side-effect Triggers navigation via MenuNavigationControllerService
   * 
   * @example
   * this.menuNavigation({name: 'Dashboard', route: '/home/dashboard'});
   * this.menuNavigation('dashboard-feature');
   */
  public menuNavigation(feature: any) {
    this.menuNavigationControllerService.navigate(feature);
  }

  /**
   * Angular lifecycle hook - After view initialization
   * 
   * Called once when the component's view has been initialized.
   * Initializes custom scrollbar for menu items using jQuery mCustomScrollbar.
   * 
   * Initialization steps:
   * 1. Checks if elements with class 'scroll' exist in the DOM
   * 2. If found, initializes mCustomScrollbar with configuration:
   *    - axis: 'y' - Vertical scrolling only
   *    - autoHideScrollbar: true - Hides scrollbar when not in use
   *    - scrollInertia: 20 - Scroll momentum/inertia value
   *    - autoScrollOnFocus: false - Disables auto-scroll on focus
   * 
   * Purpose:
   * - Provides custom styled scrollbar for menu items
   * - Improves UX with momentum scrolling
   * - Maintains consistent look across browsers
   * - Hides scrollbar to save space when not needed
   * 
   * Dependencies:
   * - jQuery (declared at module level)
   * - mCustomScrollbar jQuery plugin
   * 
   * @method ngAfterViewInit
   * @returns {void}
   * @lifecycle Angular component view initialization hook - Called once after view renders
   */
  public ngAfterViewInit(): void {
    if ($('.scroll').length > 0) {
      $('.scroll').mCustomScrollbar({
        axis: 'y',
        autoHideScrollbar: true,
        scrollInertia: 20,
        advanced: {autoScrollOnFocus: false}
      });
    }
  }

  /**
   * Detects if device is NOT a mobile device using user agent
   * 
   * Checks user agent string against common mobile browser identifiers.
   * Returns true if device is desktop/non-mobile, false if mobile.
   * 
   * Mobile user agents detected:
   * - Android phones and tablets
   * - iPhone, iPad, iPod
   * - Opera Mini
   * - Internet Explorer Mobile
   * - Windows Phone Desktop
   * 
   * Use cases:
   * - Show/hide mobile-specific UI elements
   * - Adjust navigation layout for device type
   * - Enable device-specific features
   * - Control sidebar behavior
   * 
   * @method isVisitsInMobileView
   * @returns {boolean} - True if NOT mobile (is desktop), false if is mobile device
   * 
   * @example
   * <div *ngIf="isVisitsInMobileView()">
   *   Desktop navigation
   * </div>
   */
  isVisitsInMobileView(): boolean {
    const isMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
    return !isMobile;
  }
}



